import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, ShieldCheck, Database, Scale, Timer, Fingerprint, 
  Lock, UserCheck, AlertTriangle, CreditCard
} from "lucide-react";
import { DPO_INFO } from "@/lib/security";

export default function PrivacidadePage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF33,transparent_60%)] blur-3xl" />
        <div className="absolute top-[20%] -right-40 size-[600px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC22,transparent_60%)] blur-3xl" />
        <div className="absolute bottom-[-10%] left-[10%] size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#A5C8FF44,transparent_65%)] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/10 to-[#1A66CC]/5 rounded-[2.5rem] blur-2xl opacity-50" />
          
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-12 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <ShieldCheck className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">Política de Privacidade</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Versão {DPO_INFO.versaoPolitica} · Atualizada em {DPO_INFO.ultimaAtualizacao}
                </p>
              </div>
            </header>

            <div className="prose prose-slate max-w-none space-y-10">
              <section>
                <p className="text-base text-slate-600 leading-relaxed italic">
                  Esta política descreve como o <strong className="text-slate-900 font-black uppercase tracking-tight">DataGrid XML</strong> coleta,
                  utiliza e protege dados pessoais, em conformidade com a Lei nº 13.709/2018 (LGPD).
                </p>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Database className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">1. Dados tratados</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-blue-50/50 p-5 rounded-2xl border border-blue-100/50">
                    <strong className="text-blue-900 block mb-1">Dados de cadastro</strong>
                    <p className="text-sm text-slate-600 leading-relaxed">E-mail e credenciais protegidos por criptografia de ponta a ponta via Supabase.</p>
                  </div>
                  <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-100/50">
                    <strong className="text-emerald-900 block mb-1">Dados fiscais (XMLs)</strong>
                    <p className="text-sm text-slate-600 leading-relaxed">Processados 100% no seu navegador. Nenhum conteúdo de XML é transmitido ou salvo.</p>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Scale className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">2. Bases legais (Art. 7º)</h2>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3 list-none p-0">
                  <li className="flex gap-2 text-sm text-slate-600 bg-white/40 p-3 rounded-xl border border-white/60">
                    <div className="size-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                    <span><strong>Execução de contrato:</strong> prestação do serviço analítico.</span>
                  </li>
                  <li className="flex gap-2 text-sm text-slate-600 bg-white/40 p-3 rounded-xl border border-white/60">
                    <div className="size-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                    <span><strong>Legítimo interesse:</strong> segurança e prevenção a fraude.</span>
                  </li>
                </ul>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Timer className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">3. Retenção e eliminação</h2>
                </div>
                <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Timer className="size-20" />
                  </div>
                  <p className="text-sm leading-relaxed mb-4 relative z-10">
                    XMLs e dados processados vivem apenas em <strong>memória volátil</strong>.
                  </p>
                  <p className="text-xs text-slate-400 border-t border-white/10 pt-4 relative z-10">
                    Os dados são destruídos instantaneamente no logout, fechamento da aba ou recarregamento da página.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Fingerprint className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">4. Direitos do titular (Art. 18)</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Confirmação de tratamento, acesso, correção, anonimização, portabilidade, eliminação
                  e revogação do consentimento. Para exercer, contate o Encarregado abaixo.
                </p>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Lock className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">5. Segurança técnica (Art. 46)</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    "CSP Restritiva (sem unsafe-eval)",
                    "Bloqueio de Ataques XXE",
                    "Detecção de Zip-Bomb",
                    "Row-Level Security (RLS)",
                    "Hash SHA-256 Anti-Adulteração"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs font-bold text-slate-700 bg-blue-50/30 p-3 rounded-lg border border-blue-100/30">
                      <Lock className="size-3 text-blue-400" />
                      {item}
                    </div>
                  ))}
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">6. Processamento de Pagamentos</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Para processar assinaturas, compartilhamos dados básicos (e-mail e CPF/CNPJ) com operadores de pagamento (Stripe/Asaas). 
                  <strong>Importante:</strong> Dados sensíveis de cartão de crédito são processados em ambiente seguro destes parceiros; 
                  o DataGrid XML nunca visualiza ou armazena os dados completos do seu cartão.
                </p>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">7. Exclusão de Dados</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Ao solicitar a exclusão da conta, todos os metadados de uso e dados cadastrais vinculados são eliminados permanentemente 
                  de nossos servidores em até 30 dias, salvo dados que devamos manter por obrigação legal (ex: registros de notas fiscais de serviço emitidas).
                </p>
              </section>

              <footer className="mt-16 pt-12 border-t border-blue-100/50">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <UserCheck className="size-5 text-blue-500" />
                      <h3 className="text-lg font-black text-slate-900 m-0">Encarregado (DPO)</h3>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 mb-1">{DPO_INFO.nome}</p>
                      <a href={`mailto:${DPO_INFO.email}`} className="text-sm text-blue-600 font-bold hover:underline">
                        {DPO_INFO.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="size-5 text-amber-500" />
                      <h3 className="text-lg font-black text-slate-900 m-0">Incidentes</h3>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Em caso de incidente relevante, a ANPD e os titulares serão comunicados em até <strong>48 horas</strong>.
                    </p>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
